import { Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { HandHeart, Target, TreePine, Wallet } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/lib/auth/context";

const FEATURES = [
  {
    Icon: HandHeart,
    title: "Pomoc rodzinie i zadania dodatkowe",
    body: "Część obowiązków robimy bo jesteśmy rodziną — za XP, bez stawki. Dodatkowe — wycenione w PLN, jako kieszonkowe.",
  },
  {
    Icon: Wallet,
    title: "Saldo i wypłaty",
    body: "Aplikacja księguje. Pieniądze przekazujesz dziecku jak Ci wygodnie — gotówką, przelewem, kontem dziecka.",
  },
  {
    Icon: Target,
    title: "Cele oszczędzania",
    body: "Zbieranie na konkretną rzecz — rower, klocki, grę. Dziecko widzi postęp i decyduje samo, na co wydaje.",
  },
  {
    Icon: TreePine,
    title: "Aktywność poza ekranem 🌳",
    body: "Sport, książki, pomoc na świeżym powietrzu — tag offline. Sianko docenia życie poza aplikacją, nie zwiększa screen time.",
  },
];

export default function WelcomePage() {
  const navigate = useNavigate();
  const { firebaseUser, userDoc, loading } = useAuth();

  // Jeśli zalogowany — od razu na dashboard.
  useEffect(() => {
    if (!loading && firebaseUser && userDoc) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, firebaseUser, userDoc, navigate]);

  return (
    <main className="min-h-dvh flex flex-col bg-bg text-ink relative overflow-hidden">
      <div
        className="mx-auto w-full max-w-md px-6 flex-1 flex flex-col relative"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 28px)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)",
        }}
      >
        {/* Rodzice Sianków — Tata + Mama peeking z prawego górnego, palce
            wskazują w dół na hero copy. Lekka rotacja + overlap żeby grupa
            wyglądała jak kadr rodzinnego zdjęcia. */}
        <div
          className="pointer-events-none absolute -top-2 -right-4 flex items-end z-10"
          aria-hidden
        >
          <img
            src="/parents-mascot/dad_finger_down.png"
            alt=""
            className="h-36 w-auto -mr-6 -rotate-6 drop-shadow-md"
          />
          <img
            src="/parents-mascot/mom_finger_down.png"
            alt=""
            className="h-36 w-auto rotate-3 drop-shadow-md"
          />
        </div>

        {/* Logo */}
        <div className="mb-5">
          <Logo variant="mark" size={48} />
        </div>

        {/* Hero copy */}
        <h1 className="text-[30px] leading-[1.05] font-bold tracking-tight">
          Codzienne nawyki, pierwsze pieniądze.
        </h1>
        <p className="mt-2.5 text-[14px] leading-snug text-muted">
          Część zadań robimy bo jesteśmy rodziną — bez stawki.
          Dodatkowe — z kieszonkowym. Sianko porządkuje, Wy żyjecie.
        </p>

        {/* Features — tighter spacing żeby cała sekcja + CTA mieściła się
            w jednym viewporcie 320-414px wysokości bez scrolla */}
        <ul className="mt-6 space-y-3.5">
          {FEATURES.map(({ Icon, title, body }) => (
            <li key={title} className="flex gap-3">
              <span
                className="shrink-0 mt-0.5 h-7 w-7 rounded-[8px] bg-soft flex items-center justify-center text-ink"
                aria-hidden
              >
                <Icon className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[15px] font-semibold leading-tight">
                  {title}
                </p>
                <p className="mt-0.5 text-[13px] text-muted leading-snug">
                  {body}
                </p>
              </div>
            </li>
          ))}
        </ul>

        {/* Jump-cheer — Sianko junior podskakujący w prawym dolnym rogu,
            decoracyjnie nad CTA (pointer-events-none żeby nie blokował
            klików w „Zaczynamy"). Lekko wystaje za prawą krawędź. */}
        <div
          className="pointer-events-none absolute -right-6 z-20"
          style={{
            bottom: "calc(env(safe-area-inset-bottom) + 24px)",
          }}
          aria-hidden
        >
          <img
            src="/mascot/jump-cheer.png"
            alt=""
            className="h-36 w-auto drop-shadow-md"
          />
        </div>

        {/* CTAs — przyklejone do dołu via mt-auto */}
        <div className="mt-auto pt-5 space-y-2.5">
          <Link to="/signup" className="block">
            <Button variant="accent" size="lg" block>
              Zaczynamy
            </Button>
          </Link>
          <Link to="/login" className="block">
            <Button variant="ghost" size="lg" block>
              Mam już konto
            </Button>
          </Link>
          <p className="text-center text-[10px] text-muted pt-1">
            Zakładając konto akceptujesz{" "}
            <Link to="/terms" className="underline hover:text-ink">
              Regulamin
            </Link>{" "}
            i{" "}
            <Link to="/privacy" className="underline hover:text-ink">
              Politykę Prywatności
            </Link>
            .
          </p>
        </div>
      </div>
    </main>
  );
}
